// Reader-side switching for the tabs and code-group blocks. The article body
// arrives as HTML, so the bar is built here and the panels are shown by CSS
// through `data-active` on the group.

import {
	CODE_CHECK_ICON as CHECK_ICON,
	CODE_COPY_ICON as COPY_ICON,
	enhanceCodeCopy,
	ensureCodeCopyButtons,
	handleCodeCopyClick,
	isInsideEditor,
} from "./docs-code-copy"

const READY_ATTRIBUTE = "data-panels-ready"

export interface DocsTabsLabels {
	copy: string
	copied: string
}

const DEFAULT_LABELS: DocsTabsLabels = { copy: "Copy code", copied: "Copied" }
const MAX_PANELS = 12

interface Panel {
	label: string
}

const panelChildren = (group: HTMLElement): HTMLElement[] =>
	[...group.children].filter(
		(child): child is HTMLElement => child instanceof HTMLElement,
	)

const codeLabel = (block: HTMLElement, index: number): string =>
	block.getAttribute("data-title")?.trim() ||
	block.getAttribute("data-language")?.trim() ||
	`Code ${index + 1}`

const readPanels = (group: HTMLElement): Panel[] =>
	panelChildren(group)
		.slice(0, MAX_PANELS)
		.map((child, index) => ({
			label:
				group.getAttribute("data-panel-group") === "tabs"
					? child.getAttribute("data-title")?.trim() || `Tab ${index + 1}`
					: codeLabel(child, index),
		}))

function buildBar(doc: Document, panels: Panel[], kind: string): HTMLElement {
	const bar = doc.createElement("div")
	bar.className = "docs-panel-bar"
	bar.setAttribute("role", "tablist")
	bar.setAttribute("data-panel-bar", kind)

	panels.forEach((panel, index) => {
		const button = doc.createElement("button")
		button.type = "button"
		button.className = "docs-panel-tab"
		button.setAttribute("role", "tab")
		button.setAttribute("data-panel-index", String(index + 1))
		button.setAttribute("data-panel-label", panel.label)
		button.setAttribute("aria-selected", index === 0 ? "true" : "false")
		const label = doc.createElement("span")
		label.className = "docs-panel-tab-label"
		label.textContent = panel.label
		button.appendChild(label)
		bar.appendChild(button)
	})

	return bar
}

const activeCode = (group: HTMLElement): string => {
	const index = Number(group.getAttribute("data-active") ?? "1")
	const block = panelChildren(group)[index - 1]
	return block?.querySelector("code")?.textContent ?? block?.textContent ?? ""
}

// One button for the whole group: it copies whichever panel is open, which is
// why the per-block button is hidden inside a group. Pure markup, so the same
// builder serves the live DOM and the pre-rendered HTML; clicks are delegated.
function buildCopyButton(doc: Document, labels: DocsTabsLabels): HTMLElement {
	const button = doc.createElement("button")
	button.type = "button"
	button.className = "docs-panel-copy"
	button.setAttribute("aria-label", labels.copy)
	button.innerHTML = COPY_ICON

	const slot = doc.createElement("span")
	slot.className = "docs-panel-copy-slot"
	slot.appendChild(button)
	return slot
}

function onCopyClick(
	button: HTMLElement,
	group: HTMLElement,
	labels: DocsTabsLabels,
): void {
	void navigator.clipboard
		?.writeText(activeCode(group))
		.then(() => {
			button.innerHTML = CHECK_ICON
			button.setAttribute("aria-label", labels.copied)
			setTimeout(() => {
				button.innerHTML = COPY_ICON
				button.setAttribute("aria-label", labels.copy)
			}, 2000)
		})
		.catch(() => undefined)
}

function activate(group: HTMLElement, index: number) {
	group.setAttribute("data-active", String(index))
	const bar = group.parentElement?.querySelector<HTMLElement>(
		":scope > .docs-panel-bar",
	)
	bar?.querySelectorAll<HTMLElement>(".docs-panel-tab").forEach((button) => {
		const selected = button.getAttribute("data-panel-index") === String(index)
		button.setAttribute("aria-selected", selected ? "true" : "false")
	})
}

// Matching labels move together across the page, so picking a language once
// switches every group that offers it.
function activateByLabel(container: HTMLElement, label: string) {
	container
		.querySelectorAll<HTMLElement>(`[${READY_ATTRIBUTE}="true"]`)
		.forEach((group) => {
			// Compared in JS, not through a selector: a label carries whatever the
			// author typed, and "track.ts" is not a valid selector fragment.
			const match = [
				...(group.parentElement?.querySelectorAll<HTMLElement>(
					":scope > .docs-panel-bar > .docs-panel-tab",
				) ?? []),
			].find((button) => button.getAttribute("data-panel-label") === label)
			const index = match?.getAttribute("data-panel-index")
			if (index) activate(group, Number(index))
		})
}

// A Tiptap editor draws its own bar through node views, and its children are
// node-view wrappers rather than the tab divs, so enhancing one stacked a second
// bar labelled "Tab 1" under the real one.
function isEditorOwned(group: HTMLElement): boolean {
	return isInsideEditor(group)
}

const PANEL_SELECTOR =
	'[data-panel-group], [data-type="tabs"], [data-type="code-group"]'
const BOUND_ATTRIBUTE = "data-panel-bound"

function panelKind(group: HTMLElement): string {
	// A hand-written body carries the type only, so fill in what the switching
	// needs.
	return (
		group.getAttribute("data-panel-group") ??
		(group.getAttribute("data-type") === "code-group" ? "code-group" : "tabs")
	)
}

// Idempotent: returns the bar this group already has, or wraps the group and
// builds one. The wrapper is what proves the chrome is really there, since the
// ready flag survives a React re-render that threw the bar away.
function ensureChrome(
	group: HTMLElement,
	labels: DocsTabsLabels,
): HTMLElement | null {
	const kind = panelKind(group)
	group.setAttribute("data-panel-group", kind)

	const parent = group.parentElement
	if (parent?.classList.contains("docs-panels")) {
		const existing = parent.querySelector<HTMLElement>(
			":scope > .docs-panel-bar",
		)
		if (existing) return existing
	}

	const panels = readPanels(group)
	if (panels.length === 0) return null

	const doc = group.ownerDocument
	const wrapper = doc.createElement("div")
	wrapper.className = "docs-panels"
	wrapper.setAttribute("data-panels", kind)
	group.parentElement?.insertBefore(wrapper, group)
	const bar = buildBar(doc, panels, kind)
	if (kind === "code-group") bar.appendChild(buildCopyButton(doc, labels))
	wrapper.appendChild(bar)
	wrapper.appendChild(group)

	group.setAttribute(READY_ATTRIBUTE, "true")
	activate(group, Number(group.getAttribute("data-active") ?? "1") || 1)
	return bar
}

// The bar as markup, for a body React renders itself. Baking it in means the
// tabs are there on first paint instead of waiting on an effect, so a render
// that never gets one still shows every panel rather than the first alone.
export function decorateDocsPanels(
	html: string,
	labels: DocsTabsLabels = DEFAULT_LABELS,
): string {
	if (typeof DOMParser === "undefined") return html
	if (!html.includes("data-panel-group") && !html.includes("data-type=")) {
		return html
	}
	try {
		const doc = new DOMParser().parseFromString(html, "text/html")
		doc.body
			.querySelectorAll<HTMLElement>(PANEL_SELECTOR)
			.forEach((group) => ensureChrome(group, labels))
		ensureCodeCopyButtons(doc.body, labels)
		return doc.body.innerHTML
	} catch {
		return html
	}
}

// Click handling for a bar that came in as markup. The review diff binds this
// through React, so switching a tab never waits on an effect to have run.
export function handleDocsPanelClick(
	target: HTMLElement,
	container: HTMLElement,
	labels: DocsTabsLabels = DEFAULT_LABELS,
): boolean {
	if (handleCodeCopyClick(target, labels)) return true

	const bar = target.closest<HTMLElement>(".docs-panel-bar")
	if (!bar) return false
	const group = bar.parentElement?.querySelector<HTMLElement>(
		":scope > [data-panel-group]",
	)
	// The editor draws and drives its own bar; hijacking those clicks would fight
	// the node view.
	if (!group || isEditorOwned(group)) return false

	const copy = target.closest<HTMLElement>(".docs-panel-copy")
	if (copy) {
		onCopyClick(copy, group, labels)
		return true
	}

	const button = target.closest<HTMLElement>(".docs-panel-tab")
	if (!button) return false
	const label = button.getAttribute("data-panel-label")
	if (label) {
		activateByLabel(container, label)
		return true
	}
	activate(group, Number(button.getAttribute("data-panel-index") ?? "1"))
	return true
}

export function enhanceDocsTabs(
	container: HTMLElement,
	labels: DocsTabsLabels = DEFAULT_LABELS,
): () => void {
	const cleanups: Array<() => void> = [enhanceCodeCopy(container, labels)]

	container.querySelectorAll<HTMLElement>(PANEL_SELECTOR).forEach((group) => {
		if (isEditorOwned(group)) return
		const bar = ensureChrome(group, labels)
		if (!bar) return
		// Chrome that came in as markup still needs its clicks wired, and a second
		// pass over the same bar must not stack a second listener.
		if (bar.getAttribute(BOUND_ATTRIBUTE) === "true") return
		bar.setAttribute(BOUND_ATTRIBUTE, "true")

		const onClick = (event: Event) => {
			const target = event.target as HTMLElement
			const copy = target.closest<HTMLElement>(".docs-panel-copy")
			if (copy) {
				event.preventDefault()
				onCopyClick(copy, group, labels)
				return
			}
			const button = target.closest<HTMLElement>(".docs-panel-tab")
			if (!button) return
			event.preventDefault()
			const index = Number(button.getAttribute("data-panel-index") ?? "1")
			const label = button.getAttribute("data-panel-label")
			if (label) {
				activateByLabel(container, label)
				return
			}
			activate(group, index)
		}

		bar.addEventListener("click", onClick)
		cleanups.push(() => {
			bar.removeAttribute(BOUND_ATTRIBUTE)
			bar.removeEventListener("click", onClick)
		})
	})

	return () => {
		cleanups.forEach((cleanup) => cleanup())
	}
}
